/**
 * CMS Reports tab — the APP Plus analog of the MIPS Configuration "QPP
 * Reports" tab, using the SAME look & feel (owner 2026-07-14 PM: "should look
 * exactly like QPP Reports"): blue intro card, then one Card per report with
 * icon title + description + Upload button top-right + empty-state body.
 *
 * Four sections (owner's list): ACO Eligibility File (sample on file; upload
 * screen lands with the P8 Roster tab) · Feedback Report (available soon;
 * pre-fills the improvement-score input) · CMS prior-year 479/484 reports ·
 * Registry-Computed Claims Results (tool-agnostic label; internally R4).
 */
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/components/ui/use-toast';
import {
  FileText, FileCheck2, FileBarChart2, Database, Info, Upload,
  ChevronDown, ChevronRight, Loader2, Users,
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { useAcoRosterDerived } from '@/hooks/queries/useMipsQueries';
import { qk } from '@/lib/query-keys';

interface DerivedOutputs {
  imported: boolean;
  importBatch: number | null;
  clinicians: number;
  practices: number;
  mustReport: { required: number; notRequiredLvt: number; notAvailable: number };
  qps: number;
  /** Real TIN-match failures only — masked TINs are the export's normal shape. */
  matchIssues: number;
  /** Reweight determination over MIPS ELIGIBLE CLINICIANS only (spec §7.2). */
  reweight: {
    qualified: number; mipsEcs: number; ratioPct: number | null;
    allQualified: boolean; entityThresholdPct: number;
  };
}

/**
 * P8: the LIVE eligibility-file card — uploads the QPP participant export
 * (masked or TIN-enhanced; per-row detection) through the P6 importer and
 * shows the QPP-Reports-style imported state. Re-uploading replaces the
 * year's roster transactionally.
 */
function EligibilityFileCard() {
  const { selectedAco } = useAco();
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const derivedQ = useAcoRosterDerived(selectedAco?.id ?? null, programYear);
  const derived = (derivedQ.data ?? null) as DerivedOutputs | null;
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!selectedAco) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.request(`/api/acos/${selectedAco.id}/participants/import?year=${programYear}`, {
        method: 'POST', body: form,
      });
      const summary = await res.json();
      toast({
        title: 'Roster imported',
        description: `${summary.rosterRows} clinicians · ${summary.participantTins} practices`
          + (summary.matchIssueCount > 0 ? ` · ${summary.matchIssueCount} TIN match issue${summary.matchIssueCount === 1 ? '' : 's'}` : '')
          + ` (import #${summary.batch})`,
      });
      // Shared RQ cache — Insights card / roster report pick this up too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.acoRosterDerived(selectedAco.id, programYear) }),
        queryClient.invalidateQueries({ queryKey: qk.acoRoster(selectedAco.id, programYear) }),
        queryClient.invalidateQueries({ queryKey: qk.acoParticipants(selectedAco.id, programYear) }),
      ]);
    } catch (e) {
      toast({ title: 'Import failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              ACO Eligibility File
            </CardTitle>
            <CardDescription>Clinician eligibility, must-report status, and QP scores for your ACO</CardDescription>
          </div>
          <Button disabled={uploading || isCurrentYearLocked || !selectedAco}
            onClick={() => fileRef.current?.click()}>
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {derived?.imported ? 'Replace file' : 'Upload'}
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden"
            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
        </div>
      </CardHeader>
      <CardContent>
        {derivedQ.isPending && !derived ? (
          <div className="h-16 animate-pulse rounded-lg bg-slate-100" aria-hidden />
        ) : derived?.imported ? (
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left">
                {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">PY {programYear} Participant Roster · import #{derived.importBatch}</p>
                  <p className="text-xs text-muted-foreground">
                    {derived.clinicians} clinicians · {derived.practices} practices
                    {derived.matchIssues > 0 && <> · <span className="text-amber-700">{derived.matchIssues} TIN match issue{derived.matchIssues === 1 ? '' : 's'}</span></>}
                  </p>
                </div>
              </CollapsibleTrigger>
              <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Imported</Badge>
            </div>
            <CollapsibleContent>
              <div className="mt-2 grid gap-2 rounded-lg border p-3 text-sm sm:grid-cols-2">
                <div><span className="text-muted-foreground">Must report:</span> <span className="font-medium">{derived.mustReport.required}</span></div>
                <div><span className="text-muted-foreground">Not required (LVT):</span> <span className="font-medium">{derived.mustReport.notRequiredLvt}</span></div>
                <div><span className="text-muted-foreground">Status not available:</span> <span className="font-medium">{derived.mustReport.notAvailable}</span></div>
                <div><span className="text-muted-foreground">QPs (no MIPS adjustment):</span> <span className="font-medium">{derived.qps}</span></div>
                <div>
                  <span className="text-muted-foreground">PI-reweight qualified:</span>{' '}
                  <span className="font-medium">
                    {derived.reweight.qualified} of {derived.reweight.mipsEcs} MIPS ECs ({derived.reweight.ratioPct ?? 0}%)
                    {derived.reweight.allQualified && ' — entity PI reweights automatically'}
                  </span>
                </div>
                <div className="sm:col-span-2 text-xs text-muted-foreground">
                  Full analysis on the <span className="font-medium text-foreground">Insights</span> page.
                  Re-uploading replaces this year's roster; masked (xxxxx1234) and full TINs both import.
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No eligibility file uploaded for {programYear}</p>
            <p className="mx-auto mt-2 max-w-xl text-xs">
              Upload the QPP portal participant export as-is (masked TINs) or the TIN-enhanced version —
              both import; masked TINs match on last-4 + practice name.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReportSection({
  icon, title, description, emptyLabel, note, uploadTitle,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  emptyLabel: string;
  note: React.ReactNode;
  uploadTitle: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {icon}
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Button disabled title={uploadTitle}>
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-center py-6 text-muted-foreground">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{emptyLabel}</p>
          <p className="mx-auto mt-2 max-w-xl text-xs">{note}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function CmsReportsTab() {
  const { programYear } = useProgramYear();

  return (
    <div className="space-y-6">
      {/* Intro card (same recipe as QPP Reports) */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">Upload your CMS ACO reports</p>
              <p className="text-sm text-blue-700 mt-1">
                Download your ACO's eligibility and performance reports from the{' '}
                <a href="https://qpp.cms.gov" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">
                  CMS QPP Portal
                </a>{' '}
                and upload them here — they drive eligibility, improvement-score, and claims-measure inputs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <EligibilityFileCard />
      <ReportSection
        icon={<FileCheck2 className="w-5 h-5" />}
        title="Feedback Report"
        description="CMS's ACO performance feedback — prior-year quality achievement and financial reconciliation"
        emptyLabel={`No feedback report uploaded for ${programYear}`}
        uploadTitle="Import is scoped when the first report is available"
        note={
          <>When available, this pre-fills the <span className="font-medium text-foreground">prior-year achievement %</span> that
          drives the improvement score (today entered in <span className="font-medium text-foreground">ACO Settings → Improvement score</span>).</>
        }
      />
      <ReportSection
        icon={<FileBarChart2 className="w-5 h-5" />}
        title="Administrative Claims Measure Reports"
        description="CMS's prior-year 479/484 measure reports"
        emptyLabel={`No claims measure reports uploaded for ${programYear}`}
        uploadTitle="Slot reserved — until then, enter values in Claims Measures"
        note={
          <>Will pre-fill the prior-year benchmark mode in{' '}
          <span className="font-medium text-foreground">Claims Measures</span>. Until then, enter values there directly.</>
        }
      />
      <ReportSection
        icon={<Database className="w-5 h-5" />}
        title="Registry-Computed Claims Results"
        description="Summary-level 479/484 results the registry computes independently from claims data"
        emptyLabel={`No registry-computed results uploaded for ${programYear}`}
        uploadTitle="Planned — rides with or after the ACO-upload wiring phase"
        note={
          <>Per-beneficiary event file under the CMS Administrative Claims scope; the app derives
          numerator/denominator. Results stay <span className="font-medium text-foreground">PROJECTED</span> —
          CMS's risk-standardized rate is not reproducible.</>
        }
      />
    </div>
  );
}
